import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiClient, { setLogoutCallback } from '../api/apiClient';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (loginInput: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

interface RegisterData {
  full_name: string;
  email: string;
  phone?: string;
  password: string;
}

interface LoginResponse {
  token: string;
  user: User;
}

// Utility function to check if JWT token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp;
    if (!exp) return false;
    // Check if token is expired (with 5 second buffer)
    return Date.now() >= exp * 1000 - 5000;
  } catch {
    return true; // If we can't parse, consider it expired
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    // Redirect to login page
    window.location.href = '/login';
  };

  // Set up logout callback for apiClient
  useEffect(() => {
    setLogoutCallback(logout);
  }, []);

  useEffect(() => {
    // Check for existing token on mount
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      // Check if token is expired
      if (isTokenExpired(storedToken)) {
        // Token expired, logout
        logout();
        return;
      }

      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setIsLoading(false);
  }, []);

  // Periodically check token expiration
  useEffect(() => {
    if (!token) return;

    const checkTokenExpiration = () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken && isTokenExpired(storedToken)) {
        logout();
      }
    };

    // Check every minute
    const interval = setInterval(checkTokenExpiration, 60000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (loginInput: string, password: string): Promise<void> => {
    // Skip auth for login endpoint
    const response = await apiClient.post<LoginResponse>(
      '/auth/login',
      { login: loginInput, password },
      true
    );

    if (response.token && response.user) {
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setToken(response.token);
      setUser(response.user);
    } else {
      throw new Error('Invalid response from server');
    }
  };

  const register = async (data: RegisterData): Promise<void> => {
    // Skip auth for register endpoint
    await apiClient.post('/auth/register', data, true);
  };

  const updateUser = (userData: Partial<User>) => {
    // Always merge provided fields into the existing user object if present,
    // otherwise create a new user-like object from the provided partial data.
    const updatedUser = user ? { ...user, ...userData } : ({ ...userData } as User);
    setUser(updatedUser);
    // Update localStorage so other tabs/components reading from storage stay in sync.
    try {
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch {
      // ignore storage errors
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        isLoading,
        login,
        register,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
